import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { StatusBadge } from '@/shared/ui/Badge'
import { Avatar } from '@/shared/ui/Avatar'
import { Gift, Check, Loader2 } from 'lucide-react'
import { useLoyaltyRecords, useEligibleRewardTrainers, useProcessReward } from '@/api/hooks'

const TABS = [
  { label:'Reward Programs',  path:'/dashboard/loyality',          key:'programs' },
  { label:'Eligible Trainers',path:'/dashboard/loyality/eligible', key:'eligible' },
]

interface Reward { id:number; name?:string; title?:string; points?:number; threshold?:number; is_active?:boolean; status?:string }
interface Eligible { id:number; trainer_name?:string; name?:string; reward_name?:string; status?:string; achieved_at?:string }

export function LoyaltyPage() {
  const location = useLocation()
  const tab = location.pathname.includes('eligible') ? 'eligible' : 'programs'

  const programsQ = useLoyaltyRecords(tab==='programs' ? {} : undefined)
  const eligibleQ = useEligibleRewardTrainers(tab==='eligible' ? {} : undefined)
  const process   = useProcessReward()

  const programs: Reward[]   = programsQ.data?.data ?? programsQ.data ?? []
  const eligible: Eligible[] = eligibleQ.data?.data ?? eligibleQ.data ?? []

  return (
    <div className="space-y-5">
      <PageHeader title="Loyalty Program"
        breadcrumbs={[{label:'Dashboard',to:'/dashboard'},{label:'Loyalty'}]}/>
      <div className="flex border-b border-gray-100 bg-white rounded-t-xl">
        {TABS.map(t => <NavLink key={t.path} to={t.path} end
          className={({isActive})=>`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${isActive?'border-brand-500 text-brand-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>
          {t.label}
        </NavLink>)}
      </div>

      {tab === 'programs' ? (
        <Card padding={false}>
          {programsQ.isLoading ? <Center/> : (
            <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50"><tr><th>#</th><th>Program</th><th>Points / Threshold</th><th>Status</th></tr></thead>
              <tbody>
                {programs.length === 0 ? <Empty cols={4} msg="No reward programs"/> : programs.map(r => (
                  <tr key={r.id}>
                    <td className="font-mono text-xs text-gray-400">#{r.id}</td>
                    <td className="font-medium text-gray-900 flex items-center gap-2"><Gift size={14} className="text-brand-500"/>{r.title ?? r.name ?? '—'}</td>
                    <td className="font-bold">{r.points ?? r.threshold ?? '—'}</td>
                    <td><StatusBadge status={r.status ?? (r.is_active===false?'inactive':'active')}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      ) : (
        <Card padding={false}>
          {eligibleQ.isLoading ? <Center/> : (
            <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50"><tr><th>Trainer</th><th>Reward</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {eligible.length === 0 ? <Empty cols={4} msg="No eligible trainers"/> : eligible.map(e => (
                  <tr key={e.id}>
                    <td><div className="flex items-center gap-2"><Avatar name={e.trainer_name ?? e.name ?? 'T'} size="xs"/><span className="font-medium text-gray-900">{e.trainer_name ?? e.name ?? '—'}</span></div></td>
                    <td className="text-gray-700">{e.reward_name ?? '—'}</td>
                    <td><StatusBadge status={e.status ?? 'pending'}/></td>
                    <td>
                      {(!e.status || e.status.toLowerCase()==='pending') ? (
                        <Button size="sm" icon={<Check size={13}/>} loading={process.isPending}
                          onClick={()=>process.mutate({ id:e.id, status:'APPROVED' })}>Grant</Button>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

const Center = () => <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
const Empty = ({cols,msg}:{cols:number;msg:string}) => <tr><td colSpan={cols} className="text-center text-gray-400 py-12 text-sm">{msg}</td></tr>
